const _ = require('lodash');
const { toQueryString, queryStringToObject } = require('@fiverr-private/futile/bundle');
const pathfinder = require('@fiverr-private/pathfinder');
const removeEmptyEntries = require('../../../shared/utils/removeEmptyEntries');
const { PATHFINDER, FILTER } = require('../config/filters');
const { ACTIVE_FILTER_SEPARATORS, USD_CURRENCY, SEARCH_DID_YOU_MEAN_SOURCE, SEARCH_AUTOCOMPLETE_ORIGINAL_TERM } = require('./constants');
const { normalizeActiveFilter } = require('./index');

const _queryStringToObject = (queryString) => queryString ? queryStringToObject(queryString.replace('?', '')) : {};

const PageMapping = [
    {
        condition: ({ category, subCategory, nestedSubCategory }) => (nestedSubCategory && subCategory && category),
        getResult: ({ category, subCategory, nestedSubCategory }) => ({
            page: PATHFINDER.NESTED_SUB_CATEGORY_PAGE,
            params: { category_id: category, id: subCategory, nested_sub_category_id: nestedSubCategory }
        })
    },
    {
        condition: ({ category, subCategory, nestedSubCategory }) => (!nestedSubCategory && subCategory && category),
        getResult: ({ category, subCategory }) => ({
            page: PATHFINDER.SUB_CATEGORY_PAGE,
            params: { category_id: category, id: subCategory }
        })
    },
    {
        condition: ({ category, subCategory, nestedSubCategory }) => (!nestedSubCategory && !subCategory && category),
        getResult: ({ category }) => ({
            page: PATHFINDER.CATEGORY_PAGE,
            params: { id: category }
        })
    },
    {
        condition: () => true,
        getResult: () => undefined
    }
];

const replaceUrlParam = (paramName, paramValue, url) => {
    url = (url || (window && window.location ? window.location.href : '')).replace('#!', '');

    if (paramValue === null) {
        paramValue = '';
    }
    const pattern = new RegExp(`\\b(${paramName}=).*?(&|#|$)`);

    if (url.search(pattern)>=0) {
        return url.replace(pattern, `$1${paramValue}$2`);
    }
    url = url.replace(/[?#]$/, '');
    return `${url}${(url.indexOf('?')>0 ? '&' : '?')}${paramName}=${paramValue}`;
};

const url = module.exports = {

    replaceUrlParam,

    navigateToNewUrl(queryParamKey, queryParamVal) {
        const page1Url = replaceUrlParam('page', 1);
        window.location.href = replaceUrlParam(queryParamKey, encodeURIComponent(queryParamVal), page1Url);
    },

    toQueryString(params = {}) {
        return toQueryString(removeEmptyEntries(params));
    },

    rootUrl(queryParams) {
        return pathfinder(PATHFINDER.ROOT, null, formatQueryParams(queryParams));
    },

    getPath({ PATH, PARAMS, QUERY }) {
        return pathfinder(PATH, PARAMS, formatQueryParams(QUERY));
    },

    getStoreRoute(history, { pagination, filter, sorting }) {

        const { pathname, search } = history.location;

        const currentQuery = _queryStringToObject(search);
        const newQueryParams = {
            ...currentQuery,
            page: pagination.page,
            page_size: pagination.page_size,
            offset: pagination.offset,
            filter: sorting.sortBy,
            ref: filter
        };

        const newQuery = toQueryString(removeEmptyEntries(newQueryParams));
        return `${pathname}?${newQuery}`;
    },

    staticUrl(slugs, queryParams) {
        const mapping = PageMapping.find((page) => page.condition(slugs)).getResult(slugs);

        return mapping ? pathfinder(
            mapping.page,
            mapping.params,
            formatQueryParams(queryParams)
        ) : '';
    },

    searchUrl(params) {
        const { activeFilters, filter, categoryIds, searchExperiments, isTouch } = params;
        const offset = params.page === 1 ? 0 : params.offset;
        const ref = maybeAdjustRefCurrency(params.ref, activeFilters, params.currency);

        const formattedSearchExperiments = Object.entries(searchExperiments).reduce((acc, [key, val]) => {
            acc[`exp_${key}`] = val.toString();
            return acc;
        }, {});

        const searchParams = removeEmptyEntries({
            query_string: params.query,
            page_size: params.page_size,
            page: params.page,
            offset,
            filter,
            platform: isTouch ? 'mobile_web' : 'desktop',
            browser_type: params.browser_type,
            page_ctx_id: params.page_ctx_id,
            category_id: categoryIds.categoryId,
            sub_category_id: categoryIds.subCategoryId,
            nested_sub_category_id: categoryIds.nestedSubCategoryId,
            user_id: params.currentUser.id,
            request_ip: params.request_ip,
            user_country: params.countryCode ? params.countryCode.toLowerCase() : '',
            traffic_source: params.source,
            shuffle: params.shuffle,
            pro_only: params.pro_only,
            ppc_active: params.ppc_active,
            view_type: params.view_type,
            should_suggest: params.should_suggest,
            studio_only: params.studio_only,
            promoted_ads_sanity: params.promoted_ads_sanity ? 'true' : 'false',
            user_agent: params.user_agent,
            user_guid: params.userGuid,
            ref,
            ...formattedSearchExperiments
        });

        return `${filter}?${url.toQueryString(searchParams)}`;
    },

    staticSearchUrl() {
        return pathfinder(PATHFINDER.SEARCH);
    },

    staticFilterUrl(filterUid, slugs) {

        const baseUrl = `${url.staticUrl(slugs)}`;
        const buyUrl = baseUrl.replace(`${slugs.category}/`, `${slugs.category}/buy/`);

        return `${buyUrl}/${filterUid}`;
    },

    getSubCategoryFilterUrl(history, path, params = {}) {

        const { search } = history.location;
        const currentQuery = _queryStringToObject(search);

        const newQuery = {
            ...currentQuery,
            ...params,
            page: 1,
            offset: undefined
        };

        return `${path}?${toQueryString(removeEmptyEntries(newQuery))}`;
    },

    searchInsteadUrl(query, modifiedQueryString) {
        const searchInsteadQuery = { query, modified_query: modifiedQueryString, source: SEARCH_DID_YOU_MEAN_SOURCE, should_suggest: 'false', [SEARCH_AUTOCOMPLETE_ORIGINAL_TERM]: query };
        return pathfinder(PATHFINDER.SEARCH, null, formatQueryParams(searchInsteadQuery));
    },

    relatedSearchTermsUrl(query, modifiedQueryString) {
        const relatedTermsQuery = { search_term: modifiedQueryString ? modifiedQueryString : query };
        return pathfinder(PATHFINDER.RELATED_SEARCH_TERMS, null, formatQueryParams(relatedTermsQuery));
    },

    guidedSearchNavUrl(query, prevQuery, pageCtxId) {
        return pathfinder(PATHFINDER.SEARCH, null, { query: { query, search_in: 'everywhere', source: 'guided_search_nav', ref_ctx_id: pageCtxId, prev_search_query: prevQuery } });
    }
};
const formatQueryParams = (queryParams = {}) => !_.isEmpty(queryParams) ? { query: queryParams } : undefined;

const maybeAdjustRefCurrency = (ref, activeFilters, currency) => {

    const priceRangeId = FILTER.GIG_PRICE_RANGE.ID;
    const priceRangeFilter = activeFilters[priceRangeId];

    if (!priceRangeFilter || currency.name === USD_CURRENCY) {
        return ref;
    }

    const activeFilterUSDCopy = normalizeActiveFilter(activeFilters, currency);
    const usdPriceRange = activeFilterUSDCopy[priceRangeId];

    return ref.replace(`${_priceRangeStringFormat(priceRangeFilter)}`, `${_priceRangeStringFormat(usdPriceRange)}`);
};

const _priceRangeStringFormat = (rangeValue) => `${FILTER.GIG_PRICE_RANGE.ID}${ACTIVE_FILTER_SEPARATORS.PARAM_VALUE}${rangeValue.join(ACTIVE_FILTER_SEPARATORS.ARRAY_VALUE)}`;
